import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import { isValidId } from 'utils/predicates/fields.util';

const toIdString = (value) => (isValidId(value) ? String(fromAnyToNumber(value)) : null);

const toUserId = (stylistId, userId) => toIdString(stylistId) ?? toIdString(userId);

const buildPersonalPayload = (salonId, stylistId) => {
  const sid = toIdString(stylistId);
  if (!salonId || !sid) return null;
  return { customerId: salonId, userId: sid };
};

const buildBusinessPayload = (salonId, stylistId, userId) => {
  if (!salonId) return null;
  const uid = toUserId(stylistId, userId);
  if (!uid) return null;
  return { customerId: salonId, userId: uid };
};

/**
 * Returns an ordered array of capital-session request payloads to try.
 *
 * Priority rules:
 *  - Personal offer always takes priority over business offer.
 *  - Receptionists can only see personal offers (never business).
 *  - Managers without a personal offer fall back to the business offer.
 *
 * userId is always included for DaySmart's server-side dismissal and snooze
 * tracking.  Per DaySmart, userId must be the raw stylistId (or the
 * logged-in userId when no stylistId is available) — the customerId
 * (salonId) is NOT prefixed onto userId.
 *
 * merchantId (Stripe Account ID) is NOT included here — it is passed
 * client-side as options.thirdPartyMerchantId to the SDK component.
 *
 * @param {{ salonId: string|null, stylistId: string|null, userId: string|null, isManager: boolean, isReceptionist: boolean }}
 * @returns {object[]}  Payloads in priority order (first success wins).
 */
const buildCapitalSessionPayloads = ({ salonId, stylistId, userId, isManager, isReceptionist }) => {
  const id = toIdString(salonId);
  if (!id) return [];

  const payloads = [];

  const personal = buildPersonalPayload(id, stylistId);
  if (personal) {
    payloads.push(personal);
  }

  if (isManager && !isReceptionist) {
    const business = buildBusinessPayload(id, stylistId, userId);
    if (business) {
      payloads.push(business);
    }
  }

  return payloads;
};

export default buildCapitalSessionPayloads;
